import { useState } from 'react';
import {
  LuAlertCircle,
  LuArrowRight,
  LuCheckCircle2,
  LuClock3,
  LuGlobe2,
  LuMonitorSmartphone,
  LuRefreshCw,
  LuSearchX,
} from 'react-icons/lu';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import DIYLogo from '../assets/logos/DIYDefault.svg';
import {
  getProviderAvailableSurveys,
  getProviderSurveyURl,
} from '../Actions/commonActions';

function formatCpi(cpi, currency = 'USD') {
  if (cpi === null || cpi === undefined || cpi === '') return '—';

  const amount = Number(cpi);
  if (!Number.isFinite(amount)) return '—';

  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return amount.toFixed(2);
  }
}

function SurveyCardSkeleton() {
  return (
    <div
      aria-hidden="true"
      className="animate-pulse rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6"
    >
      <div className="mb-5 flex items-center justify-between">
        <div className="h-6 w-24 rounded-full bg-slate-100" />
        <div className="h-4 w-20 rounded bg-slate-100" />
      </div>
      <div className="h-3 w-20 rounded bg-slate-100" />
      <div className="mt-3 h-5 w-3/4 rounded bg-slate-200" />
      <div className="mt-3 h-3 w-5/6 rounded bg-slate-100" />
      <div className="mt-6 grid grid-cols-3 gap-3 rounded-xl bg-slate-50 p-4">
        {[0, 1, 2].map((item) => (
          <div key={item} className="space-y-2">
            <div className="mx-auto h-3 w-1/2 rounded bg-slate-200" />
            <div className="mx-auto h-5 w-3/4 rounded bg-slate-200" />
          </div>
        ))}
      </div>
      <div className="mt-4 h-4 w-2/3 rounded bg-slate-100" />
      <div className="mt-6 h-11 rounded-xl bg-slate-200" />
    </div>
  );
}

function SurveyCard({ survey, onApply, isApplying, isAnyApplying }) {
  const surveyName =
    survey?.name || survey?.surveyName || `Survey #${survey?.surveyId ?? '—'}`;
  const hasDetails = Boolean(survey?.country || survey?.device);

  return (
    <article className="group flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition-all duration-200 hover:-translate-y-1 hover:border-indigo-200 hover:shadow-lg hover:shadow-indigo-100/50 sm:p-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        {survey?.matchType ? (
          <span className="rounded-full bg-indigo-50 px-3 py-1 text-xs font-semibold capitalize text-indigo-700">
            {survey.matchType} Match
          </span>
        ) : (
          <span className="text-xs font-medium text-slate-400">
            Survey opportunity
          </span>
        )}
        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700">
          <LuCheckCircle2 className="h-4 w-4" aria-hidden="true" />
          Available
        </span>
      </div>

      <p className="text-xs font-medium tracking-wide text-slate-400">
        SID #{survey?.surveyId ?? '—'}
      </p>
      <h3 className="mt-2 line-clamp-2 text-lg font-bold leading-snug text-slate-900">
        {surveyName}
      </h3>
      {survey?.description && (
        <p className="mt-2 line-clamp-2 text-sm leading-6 text-slate-500">
          {survey.description}
        </p>
      )}

      <div className="mt-5 grid grid-cols-3 divide-x divide-slate-200 rounded-xl bg-slate-50 py-4 text-center">
        <div className="min-w-0 px-1.5">
          <p className="text-xs text-slate-500">LOI</p>
          <p className="mt-1 text-sm font-bold text-slate-900 sm:text-base">
            {survey?.estimatedLoi ?? '—'}{' '}
            {survey?.estimatedLoi != null && (
              <span className="text-xs font-medium">min</span>
            )}
          </p>
        </div>
        <div className="min-w-0 px-1.5">
          <p className="text-xs text-slate-500">CPI</p>
          <p className="mt-1 break-words text-sm font-bold text-slate-900 sm:text-base">
            {formatCpi(survey?.cpi, survey?.currency || 'USD')}
          </p>
        </div>
        <div className="min-w-0 px-1.5">
          <p className="text-xs text-slate-500">IR</p>
          <p className="mt-1 text-sm font-bold text-slate-900 sm:text-base">
            {survey?.ir != null ? `${survey.ir}%` : '—'}
          </p>
        </div>
      </div>

      {hasDetails && (
        <div className="mt-4 flex flex-wrap gap-x-4 gap-y-2 text-xs text-slate-500">
          {survey?.country && (
            <span className="inline-flex items-center gap-1.5">
              <LuGlobe2 className="h-3.5 w-3.5" aria-hidden="true" />
              {survey.country}
            </span>
          )}
          {survey?.device && (
            <span className="inline-flex items-center gap-1.5">
              <LuMonitorSmartphone className="h-3.5 w-3.5" aria-hidden="true" />
              {survey.device}
            </span>
          )}
        </div>
      )}

      <div className="mt-auto pt-6">
        <button
          type="button"
          disabled={isAnyApplying || survey?.surveyId == null}
          onClick={() => onApply(survey.surveyId)}
          aria-label={`Take survey: ${surveyName}`}
          className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-indigo-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:cursor-not-allowed disabled:opacity-60 [&:not(:disabled)]:active:scale-[0.99]"
        >
          {isApplying ? (
            <>
              <span
                className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white"
                aria-hidden="true"
              />
              Opening survey...
            </>
          ) : (
            <>
              Take survey
              <LuArrowRight
                className="h-4 w-4 transition-transform group-hover:translate-x-0.5"
                aria-hidden="true"
              />
            </>
          )}
        </button>
      </div>
    </article>
  );
}

export default function SurveyUnavailable() {
  const { id } = useParams();
  const [applyingSurveyId, setApplyingSurveyId] = useState(null);
  const [applyError, setApplyError] = useState('');

  const { data, isLoading, isError, isFetching, refetch } = useQuery({
    queryKey: ['available-surveys', id],
    queryFn: () => getProviderAvailableSurveys({ id }),
    enabled: Boolean(id),
    retry: 1,
  });

  const surveys = Array.isArray(data?.data) ? data.data : [];
  const surveyCount = surveys.length;

  async function handleSurveyApply(surveyId) {
    if (surveyId == null || applyingSurveyId !== null) return;

    setApplyError('');
    setApplyingSurveyId(surveyId);

    try {
      const response = await getProviderSurveyURl({ id, surveyId });
      const rawUrl = response?.redirectURL ?? response?.data?.redirectURL;

      if (!rawUrl) throw new Error('Missing survey URL');

      const url = new URL(rawUrl, window.location.origin);
      if (!['http:', 'https:'].includes(url.protocol)) {
        throw new Error('Invalid survey URL');
      }

      //open link in new tab
      window.open(url.toString(), '_blank');
    } catch (error) {
      console.error('Unable to open survey:', error);
      setApplyError(
        "We couldn't open that survey. Please try again or choose another one.",
      );
    } finally {
      setApplyingSurveyId(null);
      refetch();
    }
  }

  return (
    <main className="min-h-screen bg-[#f8faff] px-4 py-6 text-slate-900 sm:px-6 sm:py-12 lg:px-8">
      <div className="mx-auto max-w-6xl">
        <header className="mb-7 flex items-center gap-2.5">
          <img src={DIYLogo} alt="Mirats Quanto" className="h-12 w-12" />
          <span className="text-lg font-bold tracking-tight">
            Mirats Quanto
          </span>
        </header>

        <section className="relative overflow-hidden rounded-3xl border border-indigo-100 bg-white px-6 py-9 shadow-sm sm:px-10 sm:py-12">
          <div className="pointer-events-none absolute -right-24 -top-28 h-72 w-72 rounded-full bg-indigo-50 blur-2xl" />
          <div className="relative flex flex-col items-start gap-5 sm:flex-row sm:gap-6">
            <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-amber-50 text-amber-600">
              <LuSearchX
                className="h-8 w-8"
                strokeWidth={1.8}
                aria-hidden="true"
              />
            </div>
            <div>
              <p className="mb-2 text-xs font-bold uppercase tracking-[0.2em] text-indigo-600">
                Survey unavailable
              </p>
              <h1 className="max-w-2xl text-2xl font-extrabold leading-tight tracking-tight sm:text-4xl">
                Unfortunately, this survey is no longer available.
              </h1>
              <p className="mt-3 max-w-2xl text-sm leading-7 text-slate-600 sm:text-base">
                It may have reached its response limit or closed. No worries —
                you can try another survey from the options below.
              </p>
            </div>
          </div>
        </section>

        <section
          className="mt-10 sm:mt-12"
          aria-labelledby="alternative-surveys"
        >
          <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
            <div>
              <p className="mb-1 text-sm font-semibold text-indigo-600">
                Keep exploring
              </p>
              <h2
                id="alternative-surveys"
                className="text-2xl font-bold tracking-tight sm:text-3xl"
              >
                Other surveys you can try
              </h2>
              <p className="mt-1.5 text-sm text-slate-500">
                Choose a survey that interests you and get started.
              </p>
            </div>
            {Boolean(id) && !isLoading && !isError && (
              <span className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-600">
                {surveyCount} {surveyCount === 1 ? 'survey' : 'surveys'}{' '}
                available
              </span>
            )}
          </div>

          {applyError && (
            <div
              role="alert"
              className="mb-5 flex items-start gap-3 rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800"
            >
              <LuAlertCircle
                className="mt-0.5 h-5 w-5 shrink-0"
                aria-hidden="true"
              />
              <span>{applyError}</span>
            </div>
          )}

          {!id ? (
            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-8 text-center">
              <LuAlertCircle
                className="mx-auto h-8 w-8 text-amber-600"
                aria-hidden="true"
              />
              <p className="mt-3 font-semibold text-slate-800">
                Survey link is incomplete
              </p>
              <p className="mt-1 text-sm text-slate-600">
                Please use a valid survey link to see available alternatives.
              </p>
            </div>
          ) : isLoading ? (
            <div
              role="status"
              aria-label="Loading available surveys"
              className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3"
            >
              {[0, 1, 2].map((item) => (
                <SurveyCardSkeleton key={item} />
              ))}
              <span className="sr-only">Loading available surveys...</span>
            </div>
          ) : isError ? (
            <div className="rounded-2xl border border-rose-200 bg-white p-8 text-center shadow-sm">
              <LuAlertCircle
                className="mx-auto h-9 w-9 text-rose-500"
                aria-hidden="true"
              />
              <p className="mt-3 font-semibold text-slate-800">
                We couldn't load available surveys
              </p>
              <p className="mt-1 text-sm text-slate-500">
                Please check your connection and try again.
              </p>
              <button
                type="button"
                onClick={() => refetch()}
                disabled={isFetching}
                className="mt-5 inline-flex min-h-10 items-center justify-center gap-2 rounded-xl bg-indigo-600 px-5 py-2 text-sm font-semibold text-white hover:bg-indigo-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <LuRefreshCw
                  className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`}
                  aria-hidden="true"
                />
                {isFetching ? 'Retrying...' : 'Try again'}
              </button>
            </div>
          ) : surveyCount > 0 ? (
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {surveys.map((survey) => (
                <SurveyCard
                  key={survey.surveyId}
                  survey={survey}
                  onApply={handleSurveyApply}
                  isApplying={applyingSurveyId === survey.surveyId}
                  isAnyApplying={applyingSurveyId !== null}
                />
              ))}
            </div>
          ) : (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center">
              <LuClock3
                className="mx-auto h-8 w-8 text-slate-400"
                aria-hidden="true"
              />
              <p className="mt-3 font-semibold text-slate-800">
                No alternatives right now
              </p>
              <p className="mt-1 text-sm text-slate-500">
                Please check back later for new surveys.
              </p>
            </div>
          )}
        </section>

        <p className="mt-10 text-center text-xs text-slate-400">
          Survey details and availability may change.
        </p>
      </div>
    </main>
  );
}
